/* eslint-disable max-len */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import AdditionalInfoTooltip from 'components/AdditionalInfoTooltip';
import CopyReferenceButton from 'components/CopyReferenceButton';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import ProductModal from 'components/ProductModal';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import StockDisplay from 'components/StockInfo';
import {
  addReferenceFromCatalogToCart,
  getLastSearchedVehicleKey,
  getLastVehicleDetail,
} from 'domains/catalog/Catalog.store';
import { DATAHUB, STANDARD } from 'domains/catalog/Catalog.types';
import { getDHReference, getPrice } from 'domains/references';
import { SupersessionTooltip } from 'pages/CatalogPage/common/ReferenceSection/SupersessionTooltip';
import {
  IconWrapper,
  EasyBasketCardWrapper,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PartsSelection.styled';
import { PriceDisplay } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PriceDisplay';
import { ReferenceProductModal } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/ReferenceProductModal';
import { Box, CenteredSpin, Empty, Flex, LinkRoundButton, MarginBox, Text } from 'UI';
import { getData } from 'utils';

interface PlateReferenceCardProps {
  referenceNumber: string;
  plateId: string;
}

const PlateReferenceCard = ({ referenceNumber, plateId }: PlateReferenceCardProps) => {
  const dispatch = useDispatch();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const reference = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const referenceData = getData(reference);
  const displaySupersessionTooltip =
    referenceData?.supersessionChainReferences && referenceData.supersessionChainReferences.length > 0;
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const priceData = getData(price);

  const handleAddToCart = () => {
    if (
      priceData?.garageView?.vatExcludedPrice &&
      priceData.garageView?.vatIncludedPrice &&
      vehicleKey &&
      getData(reference)?.isApplicableToCurrentVehicle
    ) {
      dispatch(addReferenceFromCatalogToCart({ referenceNumber, plateId }));
    }
  };

  if (!referenceData) {
    return null;
  }

  return (
    <EasyBasketCardWrapper align={'center'} minHeight={92} gap={10} justify={'space-between'} padding={'0 10px'}>
      <Flex gap={7} align={'center'} maxWidth={150} justify={'center'}>
        <ReferenceProductModal referenceNumber={referenceNumber} />
        <MarginBox mt={5}>
          <CopyReferenceButton referenceNumber={referenceNumber} />
        </MarginBox>
        <Box width={16} align={'center'}>
          <MarginBox mt={5}>
            <Box width={16} align={'center'}>
              <MarginBox mt={5}>
                <AdditionalInfoTooltip additionalInformation={referenceData.additionalInformation} />
              </MarginBox>
            </Box>
          </MarginBox>
        </Box>
      </Flex>
      <Flex gap={5}>
        <ProductModal
          triggerComponent={
            <Text type="base" disableGutter hoverUnderLine cursor={'pointer'} data-cy={'link-reference-number'}>
              {referenceData.name}
            </Text>
          }
          referenceNumber={referenceNumber}
          isVehicleCatalog
        />
      </Flex>
      <Flex gap={15} justify={'center'}>
        {displaySupersessionTooltip && (
          <ProductModal
            triggerComponent={
              <IconWrapper>
                <SupersessionTooltip>
                  <LinkRoundButton bgColor={undefined} hoverFill={undefined} size={16} />
                </SupersessionTooltip>
              </IconWrapper>
            }
            referenceNumber={referenceNumber}
            scrollToSubstitutes
          />
        )}
        <Box width={30}>
          <IconWrapper>
            <StockDisplay
              vehicleKey={vehicleDetail?.vehicleKey}
              referenceNumber={referenceNumber}
              isApplicableToCurrentVehicle={referenceData.isApplicableToCurrentVehicle}
              small
              spinSize={'default'}
            />
          </IconWrapper>
        </Box>
      </Flex>
      <DataContainer
        data={price}
        ErrorState={() => (
          <Flex maxWidth={200}>
            <ErrorWithLabel narrow />
          </Flex>
        )}
        NotFound={() => (
          <Flex maxWidth={200}>
            <Empty />
          </Flex>
        )}
        Loading={() => (
          <Flex maxWidth={200}>
            <CenteredSpin />
          </Flex>
        )}
      >
        <Flex direction={'column'} maxWidth={200}>
          <PriceDisplay priceData={priceData} />
        </Flex>
      </DataContainer>
      <ReferencePriceSection
        referenceNumber={referenceNumber}
        handleAddToCartClick={handleAddToCart}
        vehicleKey={vehicleDetail?.vehicleKey}
        isApplicableToCurrentVehicle={referenceData.isApplicableToCurrentVehicle ?? false}
        align={'left'}
        catalogSource={DATAHUB}
        referenceType={STANDARD}
        narrow
        useCompactView
        hidePrice
      />
    </EasyBasketCardWrapper>
  );
};

export default PlateReferenceCard;
